import React, { useState } from 'react';
import { Mail, Send, MessageCircle, Users, Briefcase } from 'lucide-react';
import Footer from '../components/Footer';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    contactType: 'general'
  });
  const [submitting, setSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name:         formData.name,
          email:        formData.email,
          subject:      formData.subject,
          message:      formData.message,
          contact_type: formData.contactType,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to send');
      }
      toast.success("Message sent! We'll get back to you soon.");
      setFormData({ name: '', email: '', subject: '', message: '', contactType: 'general' });
    } catch (err) {
      toast.error(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const contactTypes = [
    { value: 'general', label: 'General Inquiry', icon: <MessageCircle size={20} /> },
    { value: 'partnership', label: 'Partnership', icon: <Briefcase size={20} /> },
    { value: 'community', label: 'Community Support', icon: <Users size={20} /> },
    { value: 'press', label: 'Press & Media', icon: <Mail size={20} /> }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-24 bg-bgn-black">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 text-center">
          <h1 className="text-5xl lg:text-7xl font-bold mb-8 text-white">
            Contact Us
          </h1>
          <p className="text-xl lg:text-2xl text-bgn-grey leading-relaxed max-w-4xl mx-auto">
            Get in touch with the BGN team. We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 bg-bgn-grey/5">
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div className="bg-bgn-black rounded-lg p-8">
              <h2 className="text-3xl font-bold text-white mb-8">Send us a message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Contact Type */}
                <div>
                  <label className="block text-white font-medium mb-3">What can we help you with?</label>
                  <div className="grid grid-cols-2 gap-3">
                    {contactTypes.map((type) => (
                      <label 
                        key={type.value}
                        className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors duration-200 ${
                          formData.contactType === type.value
                            ? 'bg-bgn-gold text-bgn-black'
                            : 'bg-bgn-grey/20 text-bgn-grey hover:bg-bgn-grey/30'
                        }`}
                      >
                        <input
                          type="radio"
                          name="contactType"
                          value={type.value}
                          checked={formData.contactType === type.value}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        {type.icon}
                        <span className="text-sm font-medium">{type.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-white font-medium mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-bgn-grey/20 border border-bgn-grey/40 rounded-lg text-white placeholder-bgn-grey focus:outline-none focus:border-bgn-gold"
                    placeholder="Your full name"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-white font-medium mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-bgn-grey/20 border border-bgn-grey/40 rounded-lg text-white placeholder-bgn-grey focus:outline-none focus:border-bgn-gold"
                    placeholder="your.email@example.com"
                  />
                </div>

                {/* Subject */}
                <div>
                  <label htmlFor="subject" className="block text-white font-medium mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-bgn-grey/20 border border-bgn-grey/40 rounded-lg text-white placeholder-bgn-grey focus:outline-none focus:border-bgn-gold"
                    placeholder="Brief subject line"
                  />
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-white font-medium mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 bg-bgn-grey/20 border border-bgn-grey/40 rounded-lg text-white placeholder-bgn-grey focus:outline-none focus:border-bgn-gold resize-none"
                    placeholder="Tell us more about your inquiry..."
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-bgn-gold text-bgn-black py-3 rounded-lg font-bold hover:bg-white transition-colors duration-200 flex items-center justify-center space-x-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Send size={18} />
                  <span>{submitting ? 'Sending...' : 'Send Message'}</span>
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-white mb-8">Get in touch</h2>
                <p className="text-lg text-bgn-grey leading-relaxed mb-8">
                  We're here to help with any questions about BGN, partnerships, 
                  community support, or press inquiries. Don't hesitate to reach out!
                </p>
              </div>

              {/* Contact Methods */}
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-bgn-gold rounded-full p-3">
                    <Mail className="text-bgn-black" size={20} />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Email Us</h3>
                    <p className="text-bgn-grey">hello@blackgamersnetwork.com</p>
                    <p className="text-bgn-grey text-sm">We typically respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-bgn-purple rounded-full p-3">
                    <MessageCircle className="text-white" size={20} />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Community Discord</h3>
                    <p className="text-bgn-grey">Join our Discord server</p>
                    <p className="text-bgn-grey text-sm">Real-time chat with the community</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-white rounded-full p-3">
                    <Users className="text-bgn-black" size={20} />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Social Media</h3>
                    <p className="text-bgn-grey">Follow us on all platforms</p>
                    <p className="text-bgn-grey text-sm">@blackgamersnetwork</p>
                  </div>
                </div>
              </div>

              {/* Response Times */}
              <div className="bg-bgn-black rounded-lg p-6">
                <h3 className="text-white font-semibold mb-4">Response Times</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-bgn-grey">General Inquiries</span>
                    <span className="text-white">24-48 hours</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-bgn-grey">Partnership Requests</span>
                    <span className="text-white">1-3 business days</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-bgn-grey">Press & Media</span>
                    <span className="text-white">24 hours</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-bgn-grey">Community Support</span>
                    <span className="text-white">Same day</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;